import PageViewTemplate from '../../html/components/page-view.html'

import Component from '../shared/component'
import visit from '../shared/visit'

import {HighlightContextMixin} from './highlight'

export const PageView = Component('v-page-view', {
  inheritAttrs: false,
  mixins: [
    HighlightContextMixin
  ],
  template: PageViewTemplate,
  props: {
    source: {
      default: null
    },
    styleSheets: {
      default: null
    },
    visitors: {
      type: Object,
      default () {
        return {}
      }
    },
    selectable: {
      type: Boolean,
      default: false
    },
    selected: {
      // type: Element
      default: null
    },
    srOnlyHeading: {
      type: String
    }
  },
  data () {
    return {
      frame: null,
      loaded: false,
      hoveredNode: null,
      selectedNode: null
    }
  },
  watch: {
    frame () {
      this.render()
    },

    source () {
      this.render()
    },

    loaded () {
      this.highlight()
    },

    index () {
      this.highlight()
    },

    selected () {
      this.selectedNode = null
      this.$nextTick(() => {
        this.selectedNode = this.selected
      })
    }
  },
  mounted () {
    this.frame = this.$refs.frame
    this.selectedNode = this.selected
  },
  computed: {
    virtual () {
      const {source, visitors} = this

      if (source) {
        return visit(rebase(normalize(virtualize(source))), visitors)
      }
    }
  },
  methods: {
    render () {
      if (!this.source || !this.frame) {
        return
      }

      this.loaded = false
      this.refs = new Map()

      const {contentDocument: document} = this.frame

      // Open the document for writing. This allows us to disregard any elements
      // that might already exist in the document. As an added side effect, we
      // also force the document to re-evaluate its ready state once we close up
      // the document again.
      // NB: IE and Edge do not comply with the HTML spec and create an entirely
      // new document upon opening an existing one. We therefore have to open
      // the document BEFORE rendering as to make sure we're rendering within
      // the new document and not the old, which would otherwise lead to
      // permission errors.
      document.open()

      const rendered = render(this.virtual, this.refs, document, this.$nextTick, this.styleSheets !== null,
        {
          onMouseMove: this.onMouseMove,
          onMouseOut: this.onMouseOut,
          onClick: this.onClick
        }
      )

      switch (rendered.nodeType) {
        case rendered.DOCUMENT_NODE:
          if (rendered.doctype) {
            // Write the serialized, rather than append the rendered, doctype
            // to the document. This ensures that the document doesn't create an
            // empty doctype before appending the actual doctype. Otherwise, the
            // document will end up in quirks mode.
            document.write(serialize(rendered.doctype))
          }

          if (rendered.documentElement) {
            document.appendChild(rendered.documentElement)
          }
          break
        default:
          document.appendChild(rendered)
      }

      if (this.styleSheets !== null) {
        document.head.appendChild(document.createElement('style'))
        const styleSheet = document.styleSheets[0]

        addRules(styleSheet, rulesFromStyleSheets(this.styleSheets))
      }

      // Wait a tick before closing the document, allowing Vue to pick up the
      // associated load event even if the document is ready immediately.
      this.$nextTick(() => {
        document.close()

        // Safari doesn't play nicely with appendChild() while the document is open,
        // it appends <html> element to whatever has been appended when close() is called
        // so, remove the extraneous html tag
        const roots = [...document.childNodes].filter(n => n.tagName && n.tagName.toLowerCase() === 'html')

        for (let i = 1; i < roots.length; i++) {
          document.removeChild(roots[i])
        }
      })
    },

    highlight () {
      if (!this.loaded) {
        return
      }

      for (const [node, highlights] of this.index) {
        const ref = this.refs.get(node)

        if (ref) {
          for (const highlight of highlights) {
            highlight.attach(ref)
          }
        }
      }
    },

    onMouseMove (e) {
      if (!this.selectable) {
        return
      }

      if (this.hoveredNode === null || this.refs.get(this.hoveredNode) !== e.target) {
        this.hoveredNode = findKeyForValue(this.refs, e.target)
      }
    },

    onMouseOut (e) {
      this.hoveredNode = null
    },

    onClick (e) {
      if (!this.selectable) {
        return
      }

      e.stopPropagation()
      e.preventDefault()

      const node = findKeyForValue(this.refs, e.target)
      if (node !== this.selectedNode) {
        this.selectedNode = node
        this.$emit('select', this.selectedNode)
      }
    },

    label (node) {
      const id = node.id ? '#' + node.id : ''
      const classes = classNames(node).length ? '.' + classNames(node).join('.') : ''

      return `${tag(node)}${id}${classes}`
    }
  }
})

const EVENT_ATTRIBUTE = /^on\w+/i

function rulesFromStyleSheets (styleSheets) {
  return styleSheets
    .map(s => {
      try {
        return [...s.cssRules]
      } catch (e) {
        console.error(e, s)
      }
      return []
    })
    .reduce((a, b) => a.concat(b), [])
}
function tag (element) {
  return element.tagName.toLowerCase()
}

function classNames (element) {
  return element.className.length
    ? element.className.split(' ')
    : []
}

function children (node) {
  switch (node.nodeType) {
    case node.ELEMENT_NODE:
      return tag(node) === 'template'
        ? [node.content]
        : [...node.childNodes]
    case node.DOCUMENT_NODE:
    case node.DOCUMENT_FRAGMENT_NODE:
      return [...node.childNodes]
    default:
      return []
  }
}

function serialize (node) {
  switch (node.nodeType) {
    // https://w3c.github.io/DOM-Parsing/#xml-serializing-a-documenttype-node
    case node.DOCUMENT_TYPE_NODE:
      return `<!DOCTYPE ${node.name}` +
        (node.publicId ? ` PUBLIC "${node.publicId}"` : node.systemId ? ' SYSTEM' : '') +
        (node.systemId ? ` "${node.systemId}"` : '') +
        '>'
    default:
      return node.outerHTML
  }
}

function virtualize (node) {
  switch (node.nodeType) {
    case node.ELEMENT_NODE:
      return {
        type: 'element',
        ref: node,
        tag: tag(node),
        base: node.baseURI,
        children: children(node).map(virtualize),
        shadowRoot: node.shadowRoot && virtualize(node.shadowRoot),
        attributes: [...node.attributes].map(attribute => ({
          name: attribute.name,
          value: attribute.value,
          namespace: attribute.namespaceURI
        })),
        namespace: node.namespaceURI
      }

    case node.TEXT_NODE:
      return {
        type: 'text',
        ref: node,
        value: node.nodeValue
      }

    case node.COMMENT_NODE:
      return {
        type: 'comment',
        value: node.nodeValue
      }

    case node.DOCUMENT_NODE:
      return {
        type: 'document',
        children: children(node).map(virtualize)
      }

    case node.DOCUMENT_TYPE_NODE:
      return {
        type: 'type',
        name: node.name,
        publicId: node.publicId,
        systemId: node.systemId
      }

    case node.DOCUMENT_FRAGMENT_NODE:
      return {
        type: 'fragment',
        children: children(node).map(virtualize),
        styleSheets: [...node.styleSheets]
      }
  }
}

function normalize (node) {
  switch (node.type) {
    case 'element':
      switch (node.tag) {
        case 'html': {
          let head = node.children.find(c => c.tag === 'head')
          let body = node.children.find(c => c.tag === 'body')
          let frameset = node.children.find(c => c.tag === 'frameset')

          if (!head) {
            head = {
              type: 'element',
              tag: 'head',
              base: node.base,
              children: [],
              attributes: []
            }
          }

          if (!body) {
            body = {
              type: 'element',
              tag: 'body',
              base: node.base,
              children: [],
              attributes: []
            }
          }

          const htmlChildren = [head, body]
          if (frameset) {
            // we might end up with a document which will contain both the body and the frameset
            // but the popper relies on the body to be present, so we can't do without it
            htmlChildren.push(frameset)
          }

          return {...node, children: htmlChildren}
        }

        case 'head':
        case 'body':
          return normalize({
            type: 'element',
            tag: 'html',
            base: node.base,
            children: [node],
            attributes: []
          })

        default:
          return normalize({
            type: 'element',
            tag: 'body',
            base: node.base,
            children: [node],
            attributes: []
          })
      }
    case 'document':
      node.children = node.children.map(normalize)
  }

  return node
}

function rebase (node) {
  switch (node.type === 'element' && node.tag) {
    case 'head':
      const base = node.children.find(c => c.tag === 'base')

      if (base) {
        const target = base.attributes.find(a => a.name === 'target')

        if (target) {
          target.value = '_blank'
        } else {
          base.attributes = [
            {
              name: 'target',
              value: '_blank'
            },
            ...base.attributes
          ]
        }
      } else {
        node.children = [
          {
            type: 'element',
            tag: 'base',
            base: node.base,
            children: [],
            attributes: [
              {
                name: 'href',
                value: node.base
              },
              {
                name: 'target',
                value: '_blank'
              }
            ]
          },
          ...node.children
        ]
      }
  }

  switch (node.type) {
    case 'element':
    case 'document':
    case 'fragment':
      node.children = node.children.map(rebase)
  }

  return node
}

function obstructive (element) {
  const fixed = getComputedStyle(element).position === 'fixed'

  if (fixed) {
    if (elementOverlapsOthers(element)) {
      return false
    }
    const threshold = 0.25

    const {ownerDocument: context} = element
    const {defaultView: {innerWidth, innerHeight}} = context
    const {clientWidth, clientHeight} = element

    const totalArea = innerWidth * innerHeight
    const elementArea = clientWidth * clientHeight

    return (elementArea / totalArea) > threshold
  }

  return false
}

function elementOverlapsOthers (element) {
  const posX = (element.offsetLeft || 0) + element.clientWidth / 2
  const posY = (element.offsetTop || 0) + element.clientHeight / 2
  const { ownerDocument: doc } = element

  let elementsFromPoint = []

  if (doc.elementsFromPoint) {
    elementsFromPoint = doc.elementsFromPoint(posX, posY)
  } else if (doc.msElementsFromPoint) {
    elementsFromPoint = doc.msElementsFromPoint(posX, posY)
  }

  // weirdly enough, msElementsFromPoint can return null ¯\_(ツ)_/¯
  if (elementsFromPoint == null) {
    return true
  }

  // Start from the back of the array to go through the most outer elements first
  for (let i = elementsFromPoint.length - 1; i >= 0; i--) {
    const currentElement = elementsFromPoint[i]
    if (!element.contains(currentElement) && !['html', 'body'].includes(currentElement.nodeName.toLowerCase())) {
      return false
    }
  }
  return true
}

function render (node, refs, context, defer, skipCss, eventHandlers) {
  if (!node) {
    return
  }
  switch (node.type) {
    case 'element':
      if (node.tag === 'script') {
        return
      }

      if (skipCss) {
        if (node.tag === 'style') {
          return
        }

        // <link rel="stylesheet" ...>
        if (node.tag === 'link' && node.attributes.find(a => a.name === 'rel' && a.value === 'stylesheet')) {
          return
        }
      }

      // If the node is obstructive, do not render it. Since we output a static
      // version of the page, obstructive elements that are normally possible
      // to remove via user iteraction (e.g. loaders, modals, etc.) cannot be
      // removed and therefore permanently obstruct the page.
      if (node.ref && obstructive(node.ref)) {
        console.warn('Skipping obstructive element', node)
        return
      }

      let element
      try {
        // Don't throw up in case of invalid element names
        // https://developer.mozilla.org/en-US/docs/Web/API/Document/createElementNS#Exceptions
        element = node.namespace
          ? context.createElementNS(node.namespace, node.tag)
          : context.createElement(node.tag)
      } catch (err) {
        const logTagNameError = (tagName, err) => console.error(`Cannot create an element with tagName ${tagName}`, err)

        // if the above failed and namespace was xhtml, try again without the namespace (less strict)
        if (node.namespace && node.namespace === 'http://www.w3.org/1999/xhtml') {
          try {
            element = context.createElement(node.tag)
          } catch (err) {
            logTagNameError(node.tag, err)
            return
          }
        } else {
          logTagNameError(node.tag, err)
          return
        }
      }

      node.attributes.forEach(({name, value, namespace}) => {
        if (EVENT_ATTRIBUTE.test(name)) {
          return
        }

        switch (name) {
          case 'target':
            if (value !== '_blank') {
              return
            }
            break

          case 'href':
          case 'action':
            // Don't throw up in case of invalid URLs
            // https://developer.mozilla.org/en-US/docs/Web/API/URL/URL#Exceptions
            let url
            try {
              url = new URL(value, node.base)
            } catch (err) {}

            if (!url || url.protocol === 'javascript:') {
              return
            }
        }

        const apply = () => {
          // Don't throw up in case of invalid attribute names
          // https://developer.mozilla.org/en-US/docs/Web/API/Element/setAttribute#Exceptions
          try {
            if (namespace) {
              element.setAttributeNS(namespace, name, value)
            } else {
              element.setAttribute(name, value)
            }
          } catch (err) {}
        }

        // Certain attributes must not be added before the element has been
        // inserted into the context. This due to the fact that browsers will
        // eagerly load any external assets referenced within these attributes,
        // causing relative references to break since the element won't yet have
        // been inserted into a context with the correct <base> tag.
        if (
          name === 'style' ||
          name === 'src' ||
          (node.tag !== 'base' && name === 'href')
        ) {
          defer(apply)
        } else {
          apply()
        }
      })

      if (node.tag === 'body' && eventHandlers) {
        element.addEventListener('mousemove', eventHandlers.onMouseMove, { passive: true })
        element.addEventListener('mouseout', eventHandlers.onMouseOut, { passive: true })
        element.addEventListener('click', eventHandlers.onClick)
      }

      node.children.forEach(child => {
        const rendered = render(child, refs, context, defer, skipCss, eventHandlers)

        if (rendered) {
          element.appendChild(rendered)
        }
      })

      const shadowRootNode = node.shadowRoot
      if (shadowRootNode) {
        const shadow = element.attachShadow({ mode: 'open' })
        const shadowRootStyleSheets = shadowRootNode.styleSheets

        if (shadowRootStyleSheets) {
          const apply = () => {
            let style = context.createElement('style')
            shadow.prepend(style)
            const sheet = shadow.styleSheets[0]
            addRules(sheet, rulesFromStyleSheets(shadowRootStyleSheets))
          }
          defer(apply)
        }

        shadowRootNode.children.forEach(child => {
          const rendered = render(child, refs, context, defer)

          if (rendered) {
            shadow.appendChild(rendered)
          }
        })
      }

      if (node.ref) {
        refs.set(node.ref, element)
      }

      return element

    case 'text':
      const text = context.createTextNode(node.value)

      if (node.ref) {
        refs.set(node.ref, text)
      }

      return text

    case 'document':
      const type = node.children.find(c => c.type === 'type')
      const root = node.children.find(c => c.tag === 'html')

      const document = context.implementation.createDocument(
        '', // Don't assign a namespace; we don't assign a root element
        '', // Don't assign a root element; we append the rendered root
        type ? render(type, refs, context, defer, skipCss, eventHandlers) : null
      )

      if (root) {
        document.appendChild(render(root, refs, context, defer, skipCss, eventHandlers))
      }

      return document

    case 'type':
      return context.implementation.createDocumentType(
        node.name,
        node.publicId,
        node.systemId
      )

    case 'fragment':
      const fragment = context.createDocumentFragment()

      node.children.forEach(child => {
        const rendered = render(child, refs, context, defer, skipCss, eventHandlers)

        if (rendered) {
          fragment.appendChild(rendered)
        }
      })

      return fragment
  }
}

function addRules (styleSheet, rules) {
  rules.forEach(r => {
    const index = styleSheet.cssRules.length
    let ruleIndex
    switch (r.type) {
      case CSSRule.STYLE_RULE:// 1
        styleSheet.insertRule(r.cssText, index)
        break
      case CSSRule.IMPORT_RULE:// 3
        if (r.styleSheet) {
          ruleIndex = styleSheet.insertRule(`@media ${r.media.join(' and ')} { }`, index)
          addRules(styleSheet.cssRules[ruleIndex], [...r.styleSheet.cssRules])
        }
        break
      case CSSRule.MEDIA_RULE:// 4
        ruleIndex = styleSheet.insertRule(`@media ${r.conditionText} { }`, index)
        addRules(styleSheet.cssRules[ruleIndex], [...r.cssRules])
        break
      case CSSRule.FONT_FACE_RULE:// 5
        styleSheet.insertRule(r.cssText, index)
        break
      case CSSRule.KEYFRAMES_RULE:// 7
        styleSheet.insertRule(`@keyframes ${r.name} { ${[...r.cssRules].map(rr => rr.cssText).join(' ')} }`, index)
        break
      case CSSRule.NAMESPACE_RULE:// 10
        styleSheet.insertRule(r.cssText, index)
        break
      case CSSRule.SUPPORTS_RULE:// 12
        ruleIndex = styleSheet.insertRule(`@supports ${r.conditionText} { }`, index)
        addRules(styleSheet.cssRules[ruleIndex], [...r.cssRules])
        break
      default:
        console.warn(`unhandled type of CSSRule: ${r.type}`, r)
        break
    }
  })
}

function findKeyForValue (map, needle) {
  for (const [key, value] of map.entries()) {
    if (needle === value) {
      return key
    }
  }
}
